import React, { useEffect, useState } from 'react';
import { Alert, Box, Button, CircularProgress, Drawer, Grid, IconButton, MenuItem, TextField, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import api from '../../services/api';
import EntityPickerInline from '../ticket/EntityPickerInline';
import CustomFieldInput from '../authentication/configuracoes/shared/CustomFieldInput';
import { isFieldVisible } from '../authentication/configuracoes/shared/fieldVisibility';
import { recorrenciaResumo } from './servicoUtils';

const todayIso = () => new Date().toISOString().slice(0, 10);

// Criação de Contrato (adesão de um cliente a um Serviço) - reaproveita
// EntityPickerInline.js (já genérico, usado pelo módulo de Tickets) pro
// picker de cliente, sem duplicar o widget de busca. `dealId` é opcional -
// quando setado (aberto a partir do Deal drawer), o contrato já nasce
// vinculado a esse negócio.
const ContratoFormDrawer = ({ appId, dealId, onClose, onSaved }) => {
    const [servicos, setServicos] = useState([]);
    const [servicoId, setServicoId] = useState('');
    const [clienteSelected, setClienteSelected] = useState(null);
    const [valor, setValor] = useState('');
    const [dataInicio, setDataInicio] = useState(todayIso());
    const [dataVencimento, setDataVencimento] = useState('');
    const [observacoes, setObservacoes] = useState('');
    const [saving, setSaving] = useState(false);
    const [status, setStatus] = useState({ type: '', message: '' });

    const [fieldsConfig, setFieldsConfig] = useState([]);
    const [fields, setFields] = useState({});

    useEffect(() => {
        if (!appId) return;
        api
            .get(`v1/apps/${appId}/catalog_items/?tipo=servico&limit=200`)
            .then((response) => setServicos((response.data?.results || []).filter((s) => s.ativo)))
            .catch((error) => console.error('Erro ao carregar serviços', error));
        api
            .get(`v1/apps/${appId}/contrato_servico_fields/`)
            .then((response) => setFieldsConfig(response.data || []))
            .catch((error) => console.error('Erro ao carregar campos customizados', error));
    }, [appId]);

    const servicoSelecionado = servicos.find((s) => s.id === servicoId);

    const handleSelectServico = (id) => {
        setServicoId(id);
        const servico = servicos.find((s) => s.id === id);
        if (servico) setValor(servico.preco);
    };

    const handleSubmit = async () => {
        if (!servicoId) {
            setStatus({ type: 'error', message: 'Selecione um serviço.' });
            return;
        }
        if (!dataInicio) {
            setStatus({ type: 'error', message: 'Informe a data de início.' });
            return;
        }
        setSaving(true);
        setStatus({ type: '', message: '' });
        try {
            const payload = {
                servico: servicoId,
                cliente: clienteSelected?.id || null,
                deal: dealId || undefined,
                valor: valor || undefined,
                data_inicio: dataInicio,
                data_vencimento: dataVencimento || undefined,
                observacoes,
                fields,
            };
            await api.post(`v1/apps/${appId}/contratos_servico/`, payload);
            if (onSaved) onSaved();
        } catch (error) {
            console.error('Erro ao criar contrato', error);
            setStatus({ type: 'error', message: error.response?.data?.error || 'Erro ao criar Contrato.' });
        } finally {
            setSaving(false);
        }
    };

    return (
        <Drawer anchor="right" open onClose={onClose} PaperProps={{ sx: { width: { xs: '100%', sm: 480 }, p: 0 } }}>
            <Box sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h5" fontWeight={700}>
                        Novo Contrato
                    </Typography>
                    <IconButton onClick={onClose}>
                        <CloseIcon />
                    </IconButton>
                </Box>

                {status.message && (
                    <Alert severity={status.type} sx={{ mb: 2 }}>
                        {status.message}
                    </Alert>
                )}

                <Box display="flex" flexDirection="column" gap={2}>
                    <TextField select label="Serviço" value={servicoId} onChange={(e) => handleSelectServico(e.target.value)} fullWidth required>
                        {servicos.length === 0 ? (
                            <MenuItem disabled>Nenhum serviço ativo cadastrado</MenuItem>
                        ) : (
                            servicos.map((s) => (
                                <MenuItem key={s.id} value={s.id}>
                                    {s.nome}
                                </MenuItem>
                            ))
                        )}
                    </TextField>

                    {servicoSelecionado && (
                        <Typography variant="caption" sx={{ opacity: 0.7, mt: -1 }}>
                            {recorrenciaResumo(servicoSelecionado)}
                        </Typography>
                    )}

                    <EntityPickerInline
                        label="Cliente"
                        placeholder="Buscar cliente por nome ou e-mail..."
                        searchUrl={(term) => `v1/apps/${appId}/users/?search=${term}`}
                        extractResults={(data) => data.users}
                        getOptionLabel={(u) => `${u.first_name || ''} ${u.last_name || ''} (${u.email})`}
                        value={clienteSelected}
                        onSelect={setClienteSelected}
                        onClear={() => setClienteSelected(null)}
                        clearLabel="Sem cliente"
                    />

                    <TextField label="Valor (R$)" type="number" value={valor} onChange={(e) => setValor(e.target.value)} fullWidth />

                    <Box display="flex" gap={2}>
                        <TextField
                            label="Data de início"
                            type="date"
                            fullWidth
                            InputLabelProps={{ shrink: true }}
                            value={dataInicio}
                            onChange={(e) => setDataInicio(e.target.value)}
                        />
                        <TextField
                            label="Vencimento (opcional)"
                            type="date"
                            fullWidth
                            InputLabelProps={{ shrink: true }}
                            value={dataVencimento}
                            onChange={(e) => setDataVencimento(e.target.value)}
                            helperText={servicoSelecionado ? 'Se vazio, é calculado a partir do serviço' : ' '}
                        />
                    </Box>

                    <TextField label="Observações" value={observacoes} onChange={(e) => setObservacoes(e.target.value)} fullWidth multiline rows={2} />

                    {fieldsConfig.length > 0 && (
                        <Grid container spacing={2}>
                            {fieldsConfig
                                .filter((f) => isFieldVisible(f, fieldsConfig, fields))
                                .map((f) => (
                                    <CustomFieldInput
                                        key={f.id}
                                        field={f}
                                        value={fields[f.name]}
                                        onChange={(value) => setFields({ ...fields, [f.name]: value })}
                                    />
                                ))}
                        </Grid>
                    )}

                    <Box display="flex" justifyContent="flex-end" gap={1} mt={1}>
                        <Button variant="outlined" onClick={onClose}>
                            Cancelar
                        </Button>
                        <Button variant="contained" onClick={handleSubmit} disabled={saving}>
                            {saving ? <CircularProgress size={20} /> : 'Criar Contrato'}
                        </Button>
                    </Box>
                </Box>
            </Box>
        </Drawer>
    );
};

export default ContratoFormDrawer;
